import { ApolloError, useApolloClient } from '@apollo/client';
import { useCallback, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { invalidateCacheField } from '@/graphql/client.utils';
import { isNotFoundError } from '@/graphql/errors';
// The rows, drawer, data hook, mutation and mapping are shared with the legacy
// trowser's Liabilities step; only the chrome around them differs.
import { useLiabilitiesStepImportMutation } from '@/modules/household/LinkHouseholdDataTrowser/LiabilitiesStep/graphql/LiabilitiesStepImport.generated';
import { useLiabilityImportRecovery } from '@/modules/household/LinkHouseholdDataTrowser/LiabilitiesStep/hooks/useLiabilityImportRecovery';
import { LiabilitiesStepFormShape } from '@/modules/household/LinkHouseholdDataTrowser/LiabilitiesStep/LiabilitiesStep.types';
import {
  getSelectedLiabilityEntityUpdates,
  hasSelectedLiabilities,
  LiabilityActivationEntity,
  mapFormDataToMutation,
} from '@/modules/household/LinkHouseholdDataTrowser/LiabilitiesStep/LiabilitiesStep.utils';

import { PaneForm } from '../../../ManageIntegrationDataModal.types';

export interface LiabilitiesImportForm extends PaneForm {
  formMethods: UseFormReturn<LiabilitiesStepFormShape>;
  seed: (values: LiabilitiesStepFormShape) => void;
  setEntities: (entities: LiabilityActivationEntity[]) => void;
}

/** The "Import data" → Liabilities form (eMoney only). */
export function useLiabilitiesImportForm(
  householdId: string,
  onImported?: () => void
): LiabilitiesImportForm {
  const apolloClient = useApolloClient();
  const { showFeedback } = useFeedback();
  const successfulLiabilityIds = useRef(new Set<string>());

  const formMethods = useForm<LiabilitiesStepFormShape>({
    defaultValues: { liabilities: {} },
  });
  const recoverNotFound = useLiabilityImportRecovery(householdId, formMethods);
  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  const seed = useCallback(
    (values: LiabilitiesStepFormShape) => {
      if (isDirty) return;
      reset({
        liabilities: Object.fromEntries(
          Object.entries(values.liabilities).filter(
            ([id]) => !successfulLiabilityIds.current.has(id)
          )
        ),
      });
    },
    [isDirty, reset]
  );

  const entitiesRef = useRef<LiabilityActivationEntity[]>([]);
  const setEntities = useCallback((entities: LiabilityActivationEntity[]) => {
    entitiesRef.current = entities;
  }, []);

  // No `onError`: Apollo only rejects the promise when the caller hasn't
  // handled the error, and the commit needs that rejection to stop.
  const [importLiabilities] = useLiabilitiesStepImportMutation();

  const save = async () => {
    let canContinue = true;
    const submit = handleSubmit(
      async (values) => {
        if (!hasSelectedLiabilities(values.liabilities)) return;

        const selectedLiabilities = Object.entries(values.liabilities).filter(
          ([, liability]) => liability.shouldImport
        );
        const results = await Promise.allSettled(
          selectedLiabilities.map(async ([id, liability]) => {
            const selectedValues = { liabilities: { [id]: liability } };
            const inputs = mapFormDataToMutation(selectedValues, householdId);
            const updateEntities = getSelectedLiabilityEntityUpdates(
              selectedValues,
              entitiesRef.current
            );
            return importLiabilities({ variables: { inputs, updateEntities } });
          })
        );

        results.forEach((result, index) => {
          const liabilityId = selectedLiabilities[index]?.[0];
          if (liabilityId && result.status === 'fulfilled') {
            successfulLiabilityIds.current.add(liabilityId);
          }
        });

        if (results.some((result) => result.status === 'fulfilled')) {
          onImported?.();
        }
        invalidateCacheField('entities', apolloClient);
        invalidateCacheField('integrationLiabilities', apolloClient);

        reset({
          liabilities: Object.fromEntries(
            Object.entries(values.liabilities).filter(
              ([id]) => !successfulLiabilityIds.current.has(id)
            )
          ),
        });

        const failures = results.filter(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected'
        );
        const firstFailure = failures[0];
        if (firstFailure) {
          const missingSelectionFailure = failures.find(
            ({ reason }) =>
              reason instanceof ApolloError &&
              !reason.networkError &&
              isNotFoundError(reason)
          );
          const unexpectedFailure = failures.find(
            ({ reason }) =>
              !(reason instanceof ApolloError) ||
              reason.networkError ||
              !isNotFoundError(reason)
          );
          if (missingSelectionFailure) {
            await recoverNotFound(missingSelectionFailure.reason);
            canContinue = false;
            if (!unexpectedFailure) return;
          }
          showFeedback(
            `${failures.length} ${failures.length === 1 ? 'liability' : 'liabilities'} failed to import. Please try again.`,
            { variant: 'error' }
          );
          throw (unexpectedFailure ?? firstFailure).reason;
        }
      },
      () => {
        canContinue = false;
      }
    );

    await submit();
    return canContinue;
  };

  return { formMethods, save, seed, setEntities };
}
